/* KAAI support chat — THE LOADER (theme app extension, m21).
 * Shopify injects this file because the app embed block names it in its schema. It draws ONE button and nothing else.
 * The chat itself (support-chat.js, same extension, same CDN) is fetched when the visitor first reaches for the
 * button, so the storefront pays for a launcher and not a chat (SHAPE.md section 6: Lighthouse, 10 KB JS ceiling).
 *   · a shadow root   -> the theme's CSS cannot reach in and ours cannot leak out; no global selector exists;
 *   · no web font, no framework, no request at load, no cookie, no localStorage;
 *   · colours come from the block's JSON config: the merchant's pick, else the shop's brand colour, else ours.
 * Not minified on purpose: a merchant's developer can read what runs on their shop.
 */
(function () {
  "use strict";
  var RTL = /^(he|ar|fa|ur|yi)(-|$)/i;

  function rgb(h) {
    h = String(h || "").replace("#", "");
    if (h.length === 3) h = h.replace(/./g, "$&$&");
    if (!/^[0-9a-f]{6}$/i.test(h)) return null;
    return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)];
  }
  function hex(c) {
    return "#" + c.map(function (v) { v = Math.max(0, Math.min(255, Math.round(v))); return (v < 16 ? "0" : "") + v.toString(16); }).join("");
  }
  function lum(c) {
    var l = c.map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * l[0] + 0.7152 * l[1] + 0.0722 * l[2];
  }
  function contrast(a, b) {
    var x = lum(a), y = lum(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
  }
  function mix(c, to, t) { return c.map(function (v, i) { return v + (to[i] - v) * t; }); }

  // ⚠ A BRAND COLOUR IS CHOSEN FOR A LOGO, NOT FOR TEXT. It paints the button, but it is also the link colour on the
  //   chat's surface, and a pale brand colour there is unreadable. So: text on the accent is whichever of white or
  //   near-black contrasts more, and the link colour is the accent pushed toward the text colour until it reaches 4.5:1.
  function tokens(cfg, dark) {
    var surface = dark ? [19, 34, 39] : [241, 245, 243];       // the bot bubble: the link sits on it, and it is the harder backdrop
    var acc = rgb(cfg.accent) || [15, 107, 92];
    var on = rgb(cfg.onAccent);
    if (!on) on = contrast([255, 255, 255], acc) >= contrast([17, 17, 17], acc) ? [255, 255, 255] : [17, 17, 17];
    var ink = acc, to = dark ? [255, 255, 255] : [0, 0, 0];
    for (var i = 0; i < 24 && contrast(ink, surface) < 4.5; i++) ink = mix(ink, to, 0.08);
    var t = { "--accent": hex(acc), "--accent-ink": hex(ink), "--on-accent": hex(on) };
    if (dark) {
      t["--surface"] = "#0E1A1E"; t["--ink"] = "#E8F4F2"; t["--dim"] = "#8FAFB1"; t["--line"] = "#1C363C";
      t["--bot"] = "#132227"; t["--me"] = "#17323A"; t["--warn"] = "#FFC53D"; t["--warn-bg"] = "#231d0c";
    }
    return t;
  }

  function boot() {
    var node = document.getElementById("kaai-support-config");
    if (!node || document.getElementById("kaai-support-root")) return;
    var cfg;
    try { cfg = JSON.parse(node.textContent); } catch (e) { return; }
    if (!cfg || !cfg.t) return;

    var dark = cfg.theme === "dark" || (cfg.theme === "auto" && window.matchMedia && matchMedia("(prefers-color-scheme: dark)").matches);
    var dir = document.documentElement.getAttribute("dir") || (RTL.test(cfg.lang || document.documentElement.lang || "") ? "rtl" : "ltr");

    var host = document.createElement("div");
    host.id = "kaai-support-root";
    host.style.cssText = "all:initial";
    var tk = tokens(cfg, dark);
    for (var k in tk) host.style.setProperty(k, tk[k]);
    document.body.appendChild(host);
    var root = host.attachShadow({ mode: "open" });

    // THE SIDE IS PHYSICAL: "right" or "left". CEO 2026-10-25: «help bot its better to be on the right side».
    // The old values were "start"/"end", which are LOGICAL — on a right-to-left page like Klinika's, "end"
    // is the LEFT, so a merchant who picked "end" and a visitor reading Hebrew disagreed about where
    // the button was. "start"/"end" still work for a theme that stored them; "right" is the new default.
    var edge = cfg.side === "left" ? "left" : cfg.side === "start" ? "inset-inline-start"
             : cfg.side === "end" ? "inset-inline-end" : "right";
    root.innerHTML =
      "<style>" +
      ":host{all:initial}" +
      "*{box-sizing:border-box;font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,'Noto Sans Hebrew',Arial,sans-serif}" +
      ".sd{color:var(--ink,#17201c);font-size:16px;line-height:1.5;--tap:44px}" +
      ".launch{position:fixed;" + edge + ":20px;bottom:calc(20px + env(safe-area-inset-bottom));z-index:2147483000;" +
      "min-height:var(--tap);padding:12px 18px;border:0;border-radius:999px;background:var(--accent);color:var(--on-accent);" +
      "font-weight:600;font-size:15px;cursor:pointer;box-shadow:0 8px 24px rgb(23 32 28 / .22);touch-action:manipulation}" +
      ".launch:hover{filter:brightness(.92)}.launch[aria-busy=true]{opacity:.7;cursor:progress}" +
      ".launch:focus-visible{outline:2px solid var(--accent-ink);outline-offset:3px}" +
      "[hidden]{display:none!important}" +
      "</style>" +
      '<div class="sd" dir="' + dir + '"><button class="launch" type="button" aria-haspopup="dialog" aria-expanded="false"></button></div>';
    var launch = root.querySelector(".launch");
    launch.textContent = cfg.t.launcher;

    // THE SHOP'S CHOSEN SKIN (the Skins tab in the merchant screens). One small GET, remembered for the visit
    // (sessionStorage — this tab only, gone when it closes), applied over the colours baked in by the theme
    // editor. If the request fails nothing changes: the baked colours simply stay.
    (function () {
      var url = String(cfg.proxy || "").replace(/\/ask$/, "/skin");
      if (!/\/skin$/.test(url)) return;
      function apply(j) {
        var he = /^he|^iw/i.test(document.documentElement.lang || ""), g = j && j.greeting && (he ? j.greeting.he : j.greeting.en);
        if (g) cfg.t.greeting = g;
        var n = j && j.notice && (he ? j.notice.he : j.notice.en);
        if (n) cfg.t.ai_notice = n;                                 // reworded by the shop; the admin refuses a line that stops saying it is an AI                                   // the shop's own first line, over the theme's
        if (j && j.tokens) for (var k in j.tokens) host.style.setProperty(k, j.tokens[k]); }
      try { var c = sessionStorage.getItem("kaai.skin"); if (c) apply(JSON.parse(c)); } catch (e) { /* private mode */ }
      try {
        fetch(url, { credentials: "omit" }).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
          if (!j) return;
          apply(j);
          try { sessionStorage.setItem("kaai.skin", JSON.stringify(j)); } catch (e) { /* private mode */ }
        }).catch(function () { /* keep the baked colours */ });
      } catch (e) { /* no fetch: keep the baked colours */ }
    })();

    var chat = null, loading = false, waiting = [];
    // ⚠ A TAP THAT LANDS WHILE THE FILE IS STILL COMING MUST BE KEPT. Hover or touchstart starts the fetch, and the
    //   tap that follows used to return early and vanish, leaving the button busy for good. Every caller queues.
    function load(then) {
      if (window.KaaiSupport) return then();
      waiting.push(then);
      if (loading) return;
      loading = true;
      var s = document.createElement("script");
      s.src = cfg.chat; s.async = true;
      s.onload = function () { loading = false; var run = waiting; waiting = []; run.forEach(function (f) { f(); }); };
      s.onerror = function () { loading = false; waiting = []; launch.removeAttribute("aria-busy"); };
      document.head.appendChild(s);
    }
    function mount(open) {
      if (!window.KaaiSupport) return;
      if (!chat) chat = window.KaaiSupport.mount({ root: root, launch: launch, cfg: cfg, dir: dir });
      if (open && chat) chat.open();
    }
    // the visitor reaching for the button is the signal to fetch the chat; the click itself opens it
    var warm = function () { load(function () {}); };
    launch.addEventListener("pointerenter", warm, { once: true });
    launch.addEventListener("focus", warm, { once: true });
    launch.addEventListener("touchstart", warm, { once: true, passive: true });
    launch.addEventListener("click", function () {
      if (chat) return chat.open();
      launch.setAttribute("aria-busy", "true");
      load(function () { launch.removeAttribute("aria-busy"); mount(true); });
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();
